import React, { useState, useEffect } from 'react';
import { useThemeTokens } from '../theme/ThemeProvider';
import { Button, Card, Metric, Chart, DataTable, Progress } from './ui';

const Proveedores = () => {
  const { getComponent, getColor, getSpacing, getFontSize, getBorderRadius } = useThemeTokens();
  
  // Estados del wizard
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados de archivos
  const [ventasFile, setVentasFile] = useState(null);
  const [ventasPreview, setVentasPreview] = useState(null);
  const [marketplace, setMarketplace] = useState('Amazon');
  const [skuColumn, setSkuColumn] = useState('');
  const [idColumn, setIdColumn] = useState('');
  
  // Estados de proveedores
  const [proveedoresSource, setProveedoresSource] = useState('Archivo');
  const [proveedoresFile, setProveedoresFile] = useState(null);
  const [proveedoresPreview, setProveedoresPreview] = useState(null);
  const [proveedorColumn, setProveedorColumn] = useState('');
  const [prefijoColumn, setPrefijoColumn] = useState('');
  
  // Estados de procesamiento
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingLogs, setProcessingLogs] = useState([]);
  
  // Estados de resultados
  const [equivalencias, setEquivalencias] = useState(null);
  const [resumen, setResumen] = useState(null);

  const styles = {
    container: {
      padding: getSpacing('xl'),
      fontFamily: getComponent('typography').fontFamilies.primary,
      minHeight: '100vh',
      backgroundColor: getColor('neutral.lightGray'),
    },
    header: {
      textAlign: 'center',
      marginBottom: getSpacing('xl'),
    },
    title: {
      fontSize: getFontSize('4xl'),
      fontWeight: '700',
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md'),
    },
    subtitle: {
      fontSize: getFontSize('lg'),
      color: getColor('neutral.mediumGray'),
      maxWidth: '800px',
      margin: '0 auto',
    },
    wizardProgress: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: getSpacing('xl'),
      gap: getSpacing('md'),
    },
    stepIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      fontSize: getFontSize('sm'),
      fontWeight: '600',
      position: 'relative',
    },
    stepConnector: {
      width: '60px',
      height: '2px',
      backgroundColor: getColor('neutral.lightGray'),
    },
    stepLabel: {
      fontSize: getFontSize('sm'),
      textAlign: 'center',
      marginTop: getSpacing('xs'),
      color: getColor('neutral.mediumGray'),
    },
    stepContent: {
      backgroundColor: getColor('neutral.white'),
      padding: getSpacing('xl'),
      borderRadius: getBorderRadius('lg'),
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      maxWidth: '800px',
      margin: '0 auto',
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: getSpacing('md'),
      marginBottom: getSpacing('lg'),
    },
    formGroup: {
      marginBottom: getSpacing('md'),
    },
    label: {
      display: 'block',
      fontSize: getFontSize('sm'),
      fontWeight: '500',
      color: getColor('neutral.darkGray'),
      marginBottom: getSpacing('xs'),
    },
    input: {
      width: '100%',
      padding: getSpacing('sm'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      fontSize: getFontSize('sm'),
      backgroundColor: getColor('neutral.white'),
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: getSpacing('sm'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      fontSize: getFontSize('sm'),
      backgroundColor: getColor('neutral.white'),
      boxSizing: 'border-box',
    },
    fileUploadArea: {
      border: `2px dashed ${getColor('neutral.mediumGray')}`,
      borderRadius: getBorderRadius('md'),
      padding: getSpacing('xl'),
      textAlign: 'center',
      backgroundColor: getColor('neutral.lightGray'),
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    fileUploadActive: {
      borderColor: getColor('primary.blue'),
      backgroundColor: getColor('primary.lightBlue'),
    },
    previewContainer: {
      backgroundColor: getColor('neutral.lightGray'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginTop: getSpacing('md'),
      maxHeight: '300px',
      overflow: 'auto',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: getSpacing('xl'),
      gap: getSpacing('md'),
    },
    processingContainer: {
      textAlign: 'center',
      padding: getSpacing('xl'),
    },
    logContainer: {
      backgroundColor: getColor('neutral.lightGray'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginTop: getSpacing('md'),
      height: '200px',
      overflow: 'auto',
      fontSize: getFontSize('xs'),
      fontFamily: 'monospace',
    },
    infoCard: {
      backgroundColor: getColor('primary.lightBlue'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
      border: `1px solid ${getColor('primary.blue')}`,
    },
    warningCard: {
      backgroundColor: getColor('accent.orange'),
      color: getColor('neutral.white'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
    },
    successCard: {
      backgroundColor: getColor('accent.green'),
      color: getColor('neutral.white'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
    },
    resultsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: getSpacing('lg'),
      marginBottom: getSpacing('lg'),
    },
    metricsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: getSpacing('md'),
      marginBottom: getSpacing('lg'),
    },
  };

  // Pasos del wizard
  const steps = [
    { number: 1, title: 'Carga de Ventas', description: 'Sube el archivo de ventas del marketplace' },
    { number: 2, title: 'Carga de Proveedores', description: 'Configura la fuente de datos de proveedores' },
    { number: 3, title: 'Procesamiento', description: 'Procesa y genera las equivalencias' },
    { number: 4, title: 'Resultados', description: 'Visualiza y exporta los resultados' },
  ];

  const getStepIndicatorStyle = (stepIndex) => {
    const isActive = stepIndex === currentStep;
    const isCompleted = stepIndex < currentStep;
    
    return {
      ...styles.stepIndicator,
      backgroundColor: isCompleted ? getColor('accent.green') : 
                     isActive ? getColor('primary.blue') : getColor('neutral.lightGray'),
      color: isCompleted || isActive ? getColor('neutral.white') : getColor('neutral.mediumGray'),
    };
  };

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setProcessingLogs(prev => [...prev, `${timestamp} - ${message}`]);
  };

  // Funciones para manejo de archivos
  const handleVentasFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setVentasFile(file);
      
      // Simular preview
      const mockPreview = {
        filename: file.name,
        size: file.size,
        columns: marketplace === 'Amazon' ? 
          ['sku', 'asin', 'product-name', 'quantity', 'price'] :
          ['sku', '# de publicación', 'titulo', 'cantidad', 'precio'],
        sampleData: [
          { sku: 'GDS-0001A', id: marketplace === 'Amazon' ? 'B07XYZ123' : 'MLM123456', name: 'Producto ejemplo 1' },
          { sku: 'GDS-0002B', id: marketplace === 'Amazon' ? 'B08ABC456' : 'MLM789012', name: 'Producto ejemplo 2' },
        ]
      };
      setVentasPreview(mockPreview);
      
      // Auto-detectar columnas
      setSkuColumn(mockPreview.columns[0]);
      setIdColumn(mockPreview.columns[1]);
    }
  };

  const handleProveedoresFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProveedoresFile(file);
      
      // Simular preview
      const mockPreview = {
        filename: file.name,
        size: file.size,
        columns: ['proveedor', 'prefijo', 'categoria', 'contacto'],
        sampleData: [
          { proveedor: 'Proveedor A', prefijo: 'GDS', categoria: 'Electrónicos' },
          { proveedor: 'Proveedor B', prefijo: 'ABC', categoria: 'Hogar' },
        ]
      };
      setProveedoresPreview(mockPreview);
      
      // Auto-detectar columnas
      setProveedorColumn('proveedor');
      setPrefijoColumn('prefijo');
    }
  };

  const simulateProcessing = async () => {
    setLoading(true);
    setProcessingProgress(0);
    setProcessingLogs([]);
    
    const steps = [
      { message: 'Cargando archivo de ventas...', progress: 20 },
      { message: 'Validando formato de datos...', progress: 40 },
      { message: 'Cargando lista de proveedores...', progress: 60 },
      { message: 'Generando equivalencias...', progress: 80 },
      { message: 'Calculando resumen por proveedor...', progress: 100 },
    ];
    
    for (const step of steps) {
      addLog(step.message);
      setProcessingMessage(step.message);
      setProcessingProgress(step.progress);
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    // Generar datos mock de resultados
    const mockEquivalencias = [
      { sku: 'GDS-0001A', proveedor: 'Proveedor A', identificador: 'B07XYZ123', marketplace: marketplace },
      { sku: 'GDS-0002B', proveedor: 'Proveedor A', identificador: 'B08ABC456', marketplace: marketplace },
      { sku: 'ABC-0001C', proveedor: 'Proveedor B', identificador: 'B09DEF789', marketplace: marketplace },
    ];
    
    const mockResumen = [
      { proveedor: 'Proveedor A', cantidad_skus: 2, cantidad_identificadores: 2 },
      { proveedor: 'Proveedor B', cantidad_skus: 1, cantidad_identificadores: 1 },
    ];
    
    setEquivalencias(mockEquivalencias);
    setResumen(mockResumen);
    
    addLog('Procesamiento completado exitosamente');
    setLoading(false);
    setCurrentStep(3);
  };

  const resetWizard = () => {
    setCurrentStep(0);
    setVentasFile(null);
    setVentasPreview(null);
    setProveedoresFile(null);
    setProveedoresPreview(null);
    setEquivalencias(null);
    setResumen(null);
    setProcessingProgress(0);
    setProcessingLogs([]);
    setError(null);
  };

  // Renderizar pasos del wizard
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Paso 1: Carga de Ventas
        return (
          <div>
            <h3 style={{ fontSize: getFontSize('xl'), marginBottom: getSpacing('md') }}>
              Paso 1: Carga de Archivo de Ventas
            </h3>
            <p style={{ color: getColor('neutral.mediumGray'), marginBottom: getSpacing('lg') }}>
              Este archivo debe contener la información de ventas del marketplace (Amazon o Mercadolibre)
            </p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Selecciona el marketplace:</label>
              <select 
                value={marketplace}
                onChange={(e) => setMarketplace(e.target.value)}
                style={styles.select}
              >
                <option value="Amazon">Amazon</option>
                <option value="Mercadolibre">Mercadolibre</option>
              </select>
            </div>

            <div style={styles.infoCard}>
              <strong>Marketplace seleccionado: {marketplace}</strong>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Cargar archivo de ventas (CSV, XLS, XLSX):</label>
              <div 
                style={{
                  ...styles.fileUploadArea,
                  ...(ventasFile ? styles.fileUploadActive : {})
                }}
                onClick={() => document.getElementById('ventas-upload').click()}
              >
                <input
                  id="ventas-upload"
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={handleVentasFileUpload}
                  style={{ display: 'none' }}
                />
                {ventasFile ? (
                  <div>
                    <p>✅ Archivo seleccionado: {ventasFile.name}</p>
                    <p style={{ fontSize: getFontSize('sm') }}>
                      Tamaño: {(ventasFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p>📁 Haz clic para seleccionar el archivo de ventas</p>
                    <p style={{ fontSize: getFontSize('sm'), color: getColor('neutral.mediumGray') }}>
                      Formatos soportados: CSV, XLS, XLSX
                    </p>
                  </div>
                )}
              </div>
            </div>

            {ventasPreview && (
              <div style={styles.previewContainer}>
                <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>
                  Vista previa del archivo:
                </h4>
                <div style={styles.formGrid}>
                  <div>
                    <label style={styles.label}>Columna que contiene el SKU:</label>
                    <select 
                      value={skuColumn}
                      onChange={(e) => setSkuColumn(e.target.value)}
                      style={styles.select}
                    >
                      {ventasPreview.columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>
                      Columna que contiene el {marketplace === 'Amazon' ? 'ASIN' : '# de publicación'}:
                    </label>
                    <select 
                      value={idColumn}
                      onChange={(e) => setIdColumn(e.target.value)}
                      style={styles.select}
                    >
                      {ventasPreview.columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <DataTable 
                  data={ventasPreview.sampleData}
                  columns={Object.keys(ventasPreview.sampleData[0])}
                />
              </div>
            )}

            <div style={styles.buttonGroup}>
              <div></div>
              <Button 
                onClick={() => setCurrentStep(1)}
                disabled={!ventasFile || !skuColumn || !idColumn}
              >
                Continuar al Paso 2 →
              </Button>
            </div>
          </div>
        );

      case 1: // Paso 2: Carga de Proveedores
        return (
          <div>
            <h3 style={{ fontSize: getFontSize('xl'), marginBottom: getSpacing('md') }}>
              Paso 2: Carga de Archivo de Proveedores
            </h3>
            <p style={{ color: getColor('neutral.mediumGray'), marginBottom: getSpacing('lg') }}>
              Este archivo debe contener la lista de proveedores y sus prefijos para SKUs
            </p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Fuente de datos de proveedores:</label>
              <div style={{ display: 'flex', gap: getSpacing('md') }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: getSpacing('xs') }}>
                  <input
                    type="radio"
                    value="Archivo"
                    checked={proveedoresSource === 'Archivo'}
                    onChange={(e) => setProveedoresSource(e.target.value)}
                  />
                  Archivo
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: getSpacing('xs') }}>
                  <input
                    type="radio"
                    value="Base de datos"
                    checked={proveedoresSource === 'Base de datos'}
                    onChange={(e) => setProveedoresSource(e.target.value)}
                  />
                  Base de datos
                </label>
              </div>
            </div>

            {proveedoresSource === 'Base de datos' ? (
              <div style={styles.warningCard}>
                <strong>🚧 Conexión a Base de Datos</strong>
                <p style={{ marginTop: getSpacing('xs'), fontSize: getFontSize('sm') }}>
                  Funcionalidad en desarrollo. Por favor, usa la opción de archivo por ahora.
                </p>
              </div>
            ) : (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cargar archivo de proveedores (CSV, XLS, XLSX, JSON):</label>
                  <div 
                    style={{
                      ...styles.fileUploadArea,
                      ...(proveedoresFile ? styles.fileUploadActive : {})
                    }}
                    onClick={() => document.getElementById('proveedores-upload').click()}
                  >
                    <input
                      id="proveedores-upload"
                      type="file"
                      accept=".csv,.xls,.xlsx,.json"
                      onChange={handleProveedoresFileUpload}
                      style={{ display: 'none' }}
                    />
                    {proveedoresFile ? (
                      <div>
                        <p>✅ Archivo seleccionado: {proveedoresFile.name}</p>
                        <p style={{ fontSize: getFontSize('sm') }}>
                          Tamaño: {(proveedoresFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p>📁 Haz clic para seleccionar el archivo de proveedores</p>
                        <p style={{ fontSize: getFontSize('sm'), color: getColor('neutral.mediumGray') }}>
                          Formatos soportados: CSV, XLS, XLSX, JSON
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {proveedoresPreview && (
                  <div style={styles.previewContainer}>
                    <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>
                      Vista previa del archivo:
                    </h4>
                    <div style={styles.formGrid}>
                      <div>
                        <label style={styles.label}>Columna que contiene el nombre del proveedor:</label>
                        <select 
                          value={proveedorColumn}
                          onChange={(e) => setProveedorColumn(e.target.value)}
                          style={styles.select}
                        >
                          {proveedoresPreview.columns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={styles.label}>Columna que contiene el prefijo del proveedor:</label>
                        <select 
                          value={prefijoColumn}
                          onChange={(e) => setPrefijoColumn(e.target.value)}
                          style={styles.select}
                        >
                          {proveedoresPreview.columns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <DataTable 
                      data={proveedoresPreview.sampleData}
                      columns={Object.keys(proveedoresPreview.sampleData[0])}
                    />
                  </div>
                )}
              </>
            )}

            <div style={styles.buttonGroup}>
              <Button 
                variant="secondary"
                onClick={() => setCurrentStep(0)}
              >
                ← Volver al Paso 1
              </Button>
              <Button 
                onClick={() => setCurrentStep(2)}
                disabled={proveedoresSource === 'Archivo' && (!proveedoresFile || !proveedorColumn || !prefijoColumn)}
              >
                Continuar al Paso 3 →
              </Button>
            </div>
          </div>
        );

      case 2: // Paso 3: Procesamiento
        return (
          <div>
            <h3 style={{ fontSize: getFontSize('xl'), marginBottom: getSpacing('md') }}>
              Paso 3: Procesamiento y Generación de Equivalencias
            </h3>

            <div style={styles.infoCard}>
              <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>Configuración:</h4>
              <ul style={{ margin: 0, paddingLeft: getSpacing('lg') }}>
                <li>Marketplace: <strong>{marketplace}</strong></li>
                <li>Archivo de ventas: <strong>{ventasFile?.name}</strong></li>
                <li>Archivo de proveedores: <strong>{proveedoresFile?.name || 'Base de datos'}</strong></li>
                <li>Columna proveedor: <strong>{proveedorColumn}</strong></li>
                <li>Columna prefijo: <strong>{prefijoColumn}</strong></li>
              </ul>
            </div>

            {loading ? (
              <div style={styles.processingContainer}>
                <Progress value={processingProgress} showPercentage />
                <p style={{ marginTop: getSpacing('md'), fontSize: getFontSize('lg') }}>
                  {processingMessage}
                </p>
                
                <div style={styles.logContainer}>
                  {processingLogs.map((log, index) => (
                    <div key={index}>{log}</div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: getSpacing('xl') }}>
                <Button 
                  onClick={simulateProcessing}
                  size="large"
                >
                  🚀 Procesar archivos
                </Button>
                <p style={{ marginTop: getSpacing('md'), fontSize: getFontSize('sm'), color: getColor('neutral.mediumGray') }}>
                  Este proceso puede tomar varios minutos dependiendo del tamaño de los archivos
                </p>
              </div>
            )}

            <div style={styles.buttonGroup}>
              <Button 
                variant="secondary"
                onClick={() => setCurrentStep(1)}
                disabled={loading}
              >
                ← Volver al Paso 2
              </Button>
              <div></div>
            </div>
          </div>
        );

      case 3: // Paso 4: Resultados
        return (
          <div>
            <h3 style={{ fontSize: getFontSize('xl'), marginBottom: getSpacing('md') }}>
              Resultados: Análisis de Proveedores, SKUs e Identificadores
            </h3>

            <div style={styles.successCard}>
              ✅ Procesamiento completado exitosamente
            </div>

            {resumen && (
              <div style={styles.metricsGrid}>
                <Metric 
                  title="Total Equivalencias"
                  value={equivalencias?.length || 0}
                  format="number"
                />
                <Metric 
                  title="Proveedores"
                  value={resumen.length}
                  format="number"
                />
                <Metric 
                  title="SKUs Únicos"
                  value={new Set(equivalencias?.map(e => e.sku)).size || 0}
                  format="number"
                />
                <Metric 
                  title="Marketplace"
                  value={marketplace}
                  format="text"
                />
              </div>
            )}

            <div style={styles.resultsGrid}>
              <Card style={{ padding: getSpacing('lg') }}>
                <h4 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
                  📊 SKUs por Proveedor
                </h4>
                {resumen && (
                  <Chart
                    data={[{
                      x: resumen.map(r => r.proveedor),
                      y: resumen.map(r => r.cantidad_skus),
                      type: 'bar',
                      marker: { color: getColor('primary.blue') }
                    }]}
                    layout={{
                      title: 'Número de SKUs por Proveedor',
                      xaxis: { title: 'Proveedor' },
                      yaxis: { title: 'Número de SKUs' }
                    }}
                    height={300}
                  />
                )}
              </Card>

              <Card style={{ padding: getSpacing('lg') }}>
                <h4 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
                  📋 Tabla de Equivalencias
                </h4>
                {equivalencias && (
                  <DataTable 
                    data={equivalencias}
                    columns={['sku', 'proveedor', 'identificador', 'marketplace']}
                  />
                )}
              </Card>
            </div>

            <Card style={{ padding: getSpacing('lg') }}>
              <h4 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
                📤 Exportar Resultados
              </h4>
              <div style={{ display: 'flex', gap: getSpacing('md'), flexWrap: 'wrap' }}>
                <Button onClick={() => alert('Descarga CSV iniciada')}>
                  Descargar CSV
                </Button>
                <Button onClick={() => alert('Descarga Excel iniciada')}>
                  Descargar Excel
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => alert('Carga a BD iniciada')}
                >
                  Cargar a Base de Datos
                </Button>
              </div>
            </Card>

            <div style={styles.buttonGroup}>
              <Button 
                variant="secondary"
                onClick={resetWizard}
              >
                🔄 Volver a Empezar
              </Button>
              <div></div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          Análisis de Proveedores, SKUs e Identificadores
        </h1>
        <p style={styles.subtitle}>
          Este módulo genera un mapa de equivalencias entre proveedores, SKUs e identificadores para Amazon y Mercadolibre.
        </p>
      </div>

      {/* Indicador de progreso del wizard */}
      <div style={styles.wizardProgress}>
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div style={{ textAlign: 'center' }}>
              <div style={getStepIndicatorStyle(index)}>
                {index < currentStep ? '✓' : step.number}
              </div>
              <div style={styles.stepLabel}>
                <div style={{ fontWeight: '600' }}>{step.title}</div>
                <div style={{ fontSize: getFontSize('xs') }}>{step.description}</div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div 
                style={{
                  ...styles.stepConnector,
                  backgroundColor: index < currentStep ? getColor('accent.green') : getColor('neutral.lightGray')
                }} 
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Contenido del paso actual */}
      <div style={styles.stepContent}>
        {error && (
          <div style={styles.warningCard}>
            <strong>❌ Error:</strong> {error}
          </div>
        )}
        
        {renderStepContent()}
      </div>
    </div>
  );
};

export default Proveedores;